import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Loader2, User, X } from 'lucide-react';
import { useState } from 'react';

interface NewEncounterData {
  firstName: string;
  lastName: string;
  gender: string;
  dateOfBirth: string;
  email: string;
  phone: string;
  service: string;
  customService: string;
}

interface NewEncounterDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (data: NewEncounterData) => Promise<void>;
  isSubmitting?: boolean;
}

export default function NewEncounterDialog({
  open,
  onOpenChange,
  onSubmit,
  isSubmitting = false,
}: NewEncounterDialogProps) {
  const [formData, setFormData] = useState<NewEncounterData>({
    firstName: '',
    lastName: '',
    gender: '',
    dateOfBirth: '',
    email: '',
    phone: '',
    service: '',
    customService: '',
  });

  const [errors, setErrors] = useState<Partial<NewEncounterData>>({});

  const handleInputChange = (field: keyof NewEncounterData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Clear custom service when switching away from "other"
    if (field === 'service' && value !== 'other') {
      setFormData((prev) => ({ ...prev, customService: '' }));
    }
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<NewEncounterData> = {};

    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }

    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }

    if (!formData.gender) {
      newErrors.gender = 'Gender is required';
    }

    if (!formData.dateOfBirth) {
      newErrors.dateOfBirth = 'Date of birth is required';
    } else {
      // Validate date format (basic check)
      const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
      if (!dateRegex.test(formData.dateOfBirth)) {
        newErrors.dateOfBirth = 'Please enter a valid date';
      }
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email address is required';
    } else if (!isValidEmail(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    } else if (!isValidPhone(formData.phone)) {
      newErrors.phone = 'Please enter a valid phone number';
    }

    if (!formData.service.trim()) {
      newErrors.service = 'Encounter service is required';
    } else if (formData.service === 'Other' && !formData.customService.trim()) {
      newErrors.service = 'Please specify the custom service name';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const isValidEmail = (email: string): boolean => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const isValidPhone = (phone: string): boolean => {
    return /^[0-9]{10}$/.test(phone);
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      // Prepare data with the correct service name
      const submitData = {
        ...formData,
        service:
          formData.service === 'Other'
            ? formData.customService
            : formData.service,
      };

      await onSubmit(submitData);
      // Reset form on successful submission
      setFormData({
        firstName: '',
        lastName: '',
        gender: '',
        dateOfBirth: '',
        email: '',
        phone: '',
        service: '',
        customService: '',
      });
      setErrors({});
    } catch (error) {
      console.error('Error creating encounter:', error);
    }
  };

  const handleOpenChange = (open: boolean) => {
    if (!open && !isSubmitting) {
      // Reset form when closing
      setFormData({
        firstName: '',
        lastName: '',
        gender: '',
        dateOfBirth: '',
        email: '',
        phone: '',
        service: '',
        customService: '',
      });
      setErrors({});
    }
    onOpenChange(open);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="w-full max-w-3xl bg-white/95 dark:bg-slate-900/95 backdrop-blur-xl border border-white/20 dark:border-slate-700/40">
        <DialogHeader>
          <DialogTitle className="text-slate-800 dark:text-slate-200 font-semibold text-xl tracking-wide">
            Create Ai Encounter
          </DialogTitle>
        </DialogHeader>

        <div className="grid gap-4 py-4">
          {/* Patient Name Row */}

          <div className="flex flex-col gap-4 p-5 bg-white/50 dark:bg-slate-800/50 border border-gray-400/40 dark:border-slate-600/40 rounded-2xl shadow-sm">
            <div className="text-lg text-slate-700 dark:text-slate-300 flex items-center gap-2">
              <User className="w-4 h-4 font-semibold" />
              Patient Info
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label
                  htmlFor="firstName"
                  className="text-md font-medium text-slate-700 dark:text-slate-300 flex items-center gap-1">
                  First Name
                </Label>
                <Input
                  id="firstName"
                  value={formData.firstName}
                  onChange={(e) =>
                    handleInputChange('firstName', e.target.value)
                  }
                  className={`bg-white/50 dark:bg-slate-800/50 border-gray-400/40 dark:border-slate-600/40 ${
                    errors.firstName
                      ? 'border-red-300 dark:border-red-600'
                      : 'focus:border-purple-300 dark:focus:border-purple-600'
                  }`}
                  disabled={isSubmitting}
                />
                {errors.firstName && (
                  <p className="text-md text-red-500 dark:text-red-400">
                    {errors.firstName}
                  </p>
                )}
              </div>
              <div className="space-y-1">
                <Label
                  htmlFor="lastName"
                  className="text-md font-medium text-slate-700 dark:text-slate-300">
                  Last Name
                </Label>
                <Input
                  id="lastName"
                  value={formData.lastName}
                  onChange={(e) =>
                    handleInputChange('lastName', e.target.value)
                  }
                  className={`bg-white/50 dark:bg-slate-800/50 border-gray-400/40 dark:border-slate-600/40 ${
                    errors.lastName
                      ? 'border-red-300 dark:border-red-600'
                      : 'focus:border-purple-300 dark:focus:border-purple-600'
                  }`}
                  disabled={isSubmitting}
                />
                {errors.lastName && (
                  <p className="text-md text-red-500 dark:text-red-400">
                    {errors.lastName}
                  </p>
                )}
              </div>
            </div>

            {/* Gender and DOB Row */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label
                  htmlFor="gender"
                  className="text-md font-medium text-slate-700 dark:text-slate-300">
                  Gender
                </Label>
                <Select
                  value={formData.gender}
                  onValueChange={(value) => handleInputChange('gender', value)}
                  disabled={isSubmitting}>
                  <SelectTrigger
                    className={`bg-white/50 dark:bg-slate-800/50 border-gray-400/40 dark:border-slate-600/40 text-md ${
                      errors.gender
                        ? 'border-red-300 dark:border-red-600'
                        : 'focus:border-purple-300 dark:focus:border-purple-600'
                    }`}>
                    <SelectValue className="text-md" />
                  </SelectTrigger>
                  <SelectContent className="bg-white/95 dark:bg-slate-900/95 backdrop-blur-xl border border-gray-400/40 dark:border-slate-600/40">
                    <SelectItem value="Male" className="text-md">
                      Male
                    </SelectItem>
                    <SelectItem value="Female" className="text-md">
                      Female
                    </SelectItem>
                    <SelectItem value="Other" className="text-md">
                      Other
                    </SelectItem>
                    <SelectItem className="text-md" value="Prefer not to say">
                      Prefer not to say
                    </SelectItem>
                  </SelectContent>
                </Select>
                {errors.gender && (
                  <p className="text-md text-red-500 dark:text-red-400">
                    {errors.gender}
                  </p>
                )}
              </div>

              <div className="space-y-1">
                <Label
                  htmlFor="dateOfBirth"
                  className="text-md font-medium text-slate-700 dark:text-slate-300 flex items-center gap-1">
                  Date of Birth
                </Label>
                <Input
                  id="dateOfBirth"
                  type="date"
                  value={formData.dateOfBirth}
                  onChange={(e) =>
                    handleInputChange('dateOfBirth', e.target.value)
                  }
                  className={`bg-white/50 dark:bg-slate-800/50 border-gray-400/40 dark:border-slate-600/40 ${
                    errors.dateOfBirth
                      ? 'border-red-300 dark:border-red-600'
                      : 'focus:border-purple-300 dark:focus:border-purple-600'
                  }`}
                  disabled={isSubmitting}
                />
                {errors.dateOfBirth && (
                  <p className="text-md text-red-500 dark:text-red-400">
                    {errors.dateOfBirth}
                  </p>
                )}
              </div>
            </div>

            {/* Email and Phone Row */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label
                  htmlFor="email"
                  className="text-md font-medium text-slate-700 dark:text-slate-300">
                  Email Address
                </Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  className={`bg-white/50 dark:bg-slate-800/50 border-gray-400/40 dark:border-slate-600/40 ${
                    errors.email
                      ? 'border-red-300 dark:border-red-600'
                      : 'focus:border-purple-300 dark:focus:border-purple-600'
                  }`}
                  disabled={isSubmitting}
                />
                {errors.email && (
                  <p className="text-md text-red-500 dark:text-red-400">
                    {errors.email}
                  </p>
                )}
              </div>

              <div className="space-y-1">
                <Label
                  htmlFor="phone"
                  className="text-md font-medium text-slate-700 dark:text-slate-300">
                  Phone Number
                </Label>
                <Input
                  id="phone"
                  value={formData.phone}
                  onChange={(e) => handleInputChange('phone', e.target.value)}
                  className={`bg-white/50 dark:bg-slate-800/50 border-gray-400/40 dark:border-slate-600/40 ${
                    errors.phone
                      ? 'border-red-300 dark:border-red-600'
                      : 'focus:border-purple-300 dark:focus:border-purple-600'
                  }`}
                  disabled={isSubmitting}
                />
                {errors.phone && (
                  <p className="text-md text-red-500 dark:text-red-400">
                    {errors.phone}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Service */}
          <div className="space-y-1">
            <Label
              htmlFor="service"
              className="text-md font-medium text-slate-700 dark:text-slate-300">
              Encounter Service
            </Label>
            <Select
              value={formData.service}
              onValueChange={(value) => handleInputChange('service', value)}
              disabled={isSubmitting}>
              <SelectTrigger
                className={`bg-white/50 dark:bg-slate-800/50 border-gray-400/40 dark:border-slate-600/40 text-md ${
                  errors.service
                    ? 'border-red-300 dark:border-red-600'
                    : 'focus:border-purple-300 dark:focus:border-purple-600'
                }`}>
                <SelectValue className="text-md" />
              </SelectTrigger>
              <SelectContent className="bg-white/95 dark:bg-slate-900/95 backdrop-blur-xl border border-gray-400/40 dark:border-slate-600/40">
                <SelectItem value="Comprehensive Eye Exam" className="text-md">
                  Comprehensive Eye Exam
                </SelectItem>
                <SelectItem value="Contact Lens Fitting" className="text-md">
                  Contact Lens Fitting
                </SelectItem>
                <SelectItem value="Glaucoma Screening" className="text-md">
                  Glaucoma Screening
                </SelectItem>
                <SelectItem value="Diabetic Eye Exam" className="text-md">
                  Diabetic Eye Exam
                </SelectItem>
                <SelectItem value="Cataract Consultation" className="text-md">
                  Cataract Consultation
                </SelectItem>
                <SelectItem value="Retinal Exam" className="text-md">
                  Retinal Exam
                </SelectItem>
                <SelectItem value="Routine Follow-up" className="text-md">
                  Routine Follow-up
                </SelectItem>
                <SelectItem value="Emergency Visit" className="text-md">
                  Emergency Visit
                </SelectItem>
                <SelectItem value="Other" className="text-md">
                  Other
                </SelectItem>
              </SelectContent>
            </Select>
            {errors.service && (
              <p className="text-md text-red-500 dark:text-red-400">
                {errors.service}
              </p>
            )}
          </div>

          {/* Custom Service Input - Shows when "Other" is selected */}
          {formData.service === 'Other' && (
            <div className="space-y-1">
              <Label
                htmlFor="customService"
                className="text-md font-medium text-slate-700 dark:text-slate-300">
                Custom Service Name
              </Label>
              <Input
                id="customService"
                value={formData.customService}
                onChange={(e) =>
                  handleInputChange('customService', e.target.value)
                }
                className={`bg-white/50 dark:bg-slate-800/50 border-gray-400/40 dark:border-slate-600/40 ${
                  errors.service && formData.service === 'Other'
                    ? 'border-red-300 dark:border-red-600'
                    : 'focus:border-purple-300 dark:focus:border-purple-600'
                }`}
                disabled={isSubmitting}
              />
            </div>
          )}
        </div>

        <DialogFooter className="gap-3">
          <Button
            variant="outline"
            onClick={() => handleOpenChange(false)}
            disabled={isSubmitting}
            className="rounded-3xl bg-white/80 dark:bg-slate-800/80 border border-gray-400/40 dark:border-white/20 text-md">
            <X className="w-4 h-4 mr-2" />
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={isSubmitting}
            className="rounded-3xl bg-gradient-to-r from-purple-500 to-blue-800 hover:from-purple-500 hover:to-blue-800 text-white text-md">
            {isSubmitting ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Starting...
              </>
            ) : (
              'Create Ai Encounter'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
